import React, { useState, useEffect } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

/** Si hay más de esta cantidad de infracciones, en la lista solo mostramos las primeras 5. */
const MAX_MULTAS_VISTA = 5;

/** Frases que rotan mientras se consulta la API (cada ~8 s). Saltos de línea con \\n; el párrafo usa pre-line. */
const SABIAS_TIPS_BUSQUEDA = [
  `Las multas pueden afectar trámites como vender el auto o renovar la licencia.
Muchas personas recién las descubren cuando necesitan hacer un trámite.`,
  `Circular con la VTV vencida también es una infracción, aunque el vehículo esté en buen estado.
(Reglamentaciones provinciales y Ley 24.449)`,
  `No usar cinturón de seguridad es infracción para TODOS los ocupantes, no solo el conductor.
(Ley 24.449, art. 40 inc. k)`,
  `Está prohibido disminuir arbitrariamente y bruscamente la velocidad, realizar movimientos zigzagueantes o maniobras caprichosas e intempestivas.`,
  `Podés recibir una multa por un vehículo que no es el tuyo.
Puede ocurrir por errores de lectura o uso indebido de patentes`,
  `Las cámaras registran hechos, pero no el contexto.
Por ejemplo, no evalúan si la señalización era correcta o visible`,
  `Una multa puede dejar de verse en el sistema… pero seguir activa.
Cuando está en trámite, puede desaparecer visualmente sin estar resuelta.`,
  `Si no hacés nada frente a una multa, el proceso puede avanzar igual y quedar firme.
No intervenir puede derivar en resolución en ausencia`,
  `Pagar una multa con descuento implica reconocer la infracción y generar antecedentes.
No es solo "cerrar el tema", tiene consecuencias legales futuras`,
  `El titular del vehículo es responsable de la infracción, aunque no estuviera manejando.
Esto se debe a que el sistema vincula automáticamente la multa al dominio del vehículo.`,
  `En avenidas podés circular hasta 60 km/h y en calles hasta 40 km/h.
Si ponen un radar con una velocidad más baja, tiene que estar señalizado 500 metros antes.`,
  `En autopistas o semiautopistas, los radares no tienen que estar señalizados con distancia previa.
Solo alcanza con un cartel general que indique que hay controles de velocidad en la vía.`,
  `El control de alcoholemia tiene requisitos legales.
Si el dispositivo no está homologado correctamente, la infracción puede ser cuestionable.`,
  `La app Mi Argentina no reemplaza tu registro.
La licencia digital es válida como complemento, pero el documento físico sigue siendo obligatorio.`,
  `Si se te venció la licencia, ya no podés manejar.
No existe ningún "período de gracia" para circular.
Los 90 días (o el plazo que tenga tu distrito)
son solo para renovar la licencia sin rendir todos los exámenes.`,
  `No podés evitar un control de alcoholemia.

Negarte, escaparte o impedir el control
puede generar inhabilitación para conducir.`,
  `Negarte a un control de alcoholemia puede dejarte sin licencia por 18 meses.

La ley sanciona la negativa igual que los casos más graves.`,
];

const ROTACION_SABIAS_MS = 8000;

/** Índice al azar; con `exclude` no repite el mismo (si hay al menos 2 tips). */
function pickRandomSabiasIndex(exclude) {
  const len = SABIAS_TIPS_BUSQUEDA.length;
  if (len <= 1) return 0;
  let next;
  let guard = 0;
  do {
    next = Math.floor(Math.random() * len);
    guard += 1;
  } while (next === exclude && guard < len * 4);
  return next;
}

/** Vacío = rutas relativas /api/… (en dev, proxy de package.json → Express :5000). Si el API está en otro dominio, usá REACT_APP_API_BASE_URL. */
function getApiBaseUrl() {
  const u = process.env.REACT_APP_API_BASE_URL;
  if (u && String(u).trim()) return String(u).replace(/\/$/, '');
  return '';
}

/** Normaliza teléfono para la API (prefiere +549… según documentación). */
function normalizeTelefonoForApi(raw) {
  const trimmed = String(raw || '').trim();
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';
  if (trimmed.startsWith('+')) return `+${digits}`;
  if (digits.startsWith('54')) return `+${digits}`;
  // UX del formulario: el usuario carga solo el número local y nosotros anteponemos +549.
  return `+549${digits}`;
}

const WHATSAPP_RETRY_DELAY_MS = 1500;
const WHATSAPP_REQUEST_TIMEOUT_MS = 125_000;

/** Mensaje para el usuario: nunca texto técnico del upstream (ej. "Error consultando API…"). */
function userMessageForWhatsappFailure(data, res) {
  const code = data?.code;
  if (code === 'SCRAPER_TIMEOUT' || res.status === 502) {
    return 'El servicio tardó demasiado. Esperá unos minutos y volvé a intentar.';
  }
  if (res.status === 504 && code === 'PROXY_TIMEOUT') {
    return 'La consulta superó el tiempo de espera. Intentá de nuevo en unos minutos.';
  }
  return 'No pudimos completar el envío. Por favor, intentá de nuevo en unos minutos.';
}

async function fetchConsultaWhatsappOnce(apiBaseUrl, body) {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), WHATSAPP_REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${apiBaseUrl}/api/v1/consulta-whatsapp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const text = await res.text();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = {};
    }
    return { res, data };
  } finally {
    clearTimeout(tid);
  }
}

const ConsultaMultas = () => {
  const [patente, setPatente] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resultados, setResultados] = useState(null);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappNotice, setWhatsappNotice] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    cupon: '',
    tipoCliente: 'particulares',
  });
  const [ref, isVisible] = useScrollAnimation();
  const [sabiasIndex, setSabiasIndex] = useState(0);
  const [isMobileCompact, setIsMobileCompact] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767.98px)');
    const apply = () => setIsMobileCompact(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    if (!loading) return undefined;
    setSabiasIndex(pickRandomSabiasIndex());
    const id = setInterval(() => {
      setSabiasIndex((prev) => pickRandomSabiasIndex(prev));
    }, ROTACION_SABIAS_MS);
    return () => clearInterval(id);
  }, [loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResultados(null);
    setWhatsappNotice(null);
    setLoading(true);

    try {
      const cleanPatente = patente.toUpperCase().replace(/\s+/g, '');
      const apiBaseUrl = getApiBaseUrl();
      const res = await fetch(`${apiBaseUrl}/api/v1/consulta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patente: cleanPatente }),
      });

      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = { raw: text };
      }

      if (!res.ok) {
        const msg =
          data?.message ||
          data?.error ||
          data?.detail ||
          (text && text.length < 400 ? text : null) ||
          `Error ${res.status}`;
        throw new Error(String(msg));
      }

      // Consolidación mínima (solo cantidad + descripción)
      const multas = [];
      const fase1 = data?.fase1 || {};
      ['pba', 'caba', 'mda'].forEach((k) => {
        const j = fase1[k];
        if (j?.status === 'ok' && Array.isArray(j.multas)) {
          j.multas.forEach((m) => {
            multas.push({
              descripcion: m?.descripcion || 'Infracción de tránsito',
            });
          });
        }
      });

      const cantidadMultas = data?.resumen?.total_multas ?? multas.length;
      const multasMostradas =
        cantidadMultas > MAX_MULTAS_VISTA
          ? multas.slice(0, MAX_MULTAS_VISTA)
          : multas;
      const hayMasEnPantalla = cantidadMultas > multasMostradas.length;

      setResultados({
        patente: data?.patente || cleanPatente,
        cantidadMultas,
        sinMultas: cantidadMultas === 0,
        multas,
        multasMostradas,
        hayMasEnPantalla,
      });
    } catch (err) {
      setError(err?.message || 'Ocurrió un error. Por favor, intentá nuevamente.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsappSubmit = async (e) => {
    e.preventDefault();
    if (!resultados?.patente) return;

    setWhatsappNotice(null);
    setWhatsappLoading(true);

    const apiBaseUrl = getApiBaseUrl();
    const telefono = normalizeTelefonoForApi(formData.telefono);
    // Solo estos campos van a la API de WhatsApp; cupón es UI (no se envía hasta habilitarlo en backend).
    const body = {
      patente: resultados.patente,
      telefono,
      nombre_cliente: formData.nombre.trim(),
    };

    try {
      let last = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        if (attempt > 0) {
          await new Promise((r) => setTimeout(r, WHATSAPP_RETRY_DELAY_MS));
        }
        try {
          last = await fetchConsultaWhatsappOnce(apiBaseUrl, body);
        } catch (fetchErr) {
          if (attempt === 0) {
            console.warn('[consulta-whatsapp] primer intento (red/timeout), reintentando', fetchErr);
            continue;
          }
          console.error('[consulta-whatsapp] error tras reintento', fetchErr);
          const aborted = fetchErr?.name === 'AbortError';
          setWhatsappNotice({
            variant: 'warning',
            message: aborted
              ? 'La solicitud tardó demasiado. Intentá de nuevo en unos minutos.'
              : 'No pudimos conectar. Intentá de nuevo en unos minutos.',
          });
          return;
        }

        if (last.data.success) {
          setWhatsappNotice({
            variant: 'success',
            message:
              last.data.whatsapp_enviado === false
                ? 'Listo. Te escribimos por WhatsApp en breve.'
                : 'Presupuesto enviado',
          });
          setFormData({ nombre: '', telefono: '', cupon: '', tipoCliente: 'particulares' });
          return;
        }

        if (attempt === 0) {
          console.warn('[consulta-whatsapp] API sin éxito, reintentando', last.data?.code, last.res.status);
          continue;
        }

        console.error('[consulta-whatsapp] sin éxito tras reintento', last.data, last.res.status);
        setWhatsappNotice({
          variant: 'warning',
          message: userMessageForWhatsappFailure(last.data, last.res),
        });
      }
    } finally {
      setWhatsappLoading(false);
    }
  };

  return (
    <section id="consulta-multas" className="bg-white" style={{ paddingTop: '5rem', paddingBottom: '3rem' }}>
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-8 col-xl-7 mx-auto" ref={ref}>
            <div className={`text-center mb-4 ${isVisible ? 'animate-fade-in' : ''}`}>
              <h2 className="fw-bold mb-3 titulo-seccion-desktop">Consultá tus Fotomultas</h2>
              <p className="text-muted">Ingresá la patente de tu vehículo para conocer el estado judicial de tus fotomultas</p>
            </div>
            
            <div className={`card shadow-lg border-0 p-4 ${isVisible ? 'animate-on-scroll' : ''}`} style={{ animationDelay: '0.2s' }}>
              <form onSubmit={handleSubmit}>
                <div className="d-flex flex-column flex-md-row gap-3">
                  <div className="flex-grow-1 position-relative">
                    <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" style={{ fontSize: '1.5rem' }}></i>
                    <input
                      type="text"
                      className="form-control form-control-lg ps-5"
                      placeholder={
                        isMobileCompact ? 'AB 123 CD' : 'Ingresá tu patente (ej: ABC123)'
                      }
                      value={patente}
                      onChange={(e) => setPatente(e.target.value.toUpperCase())}
                      style={{ fontSize: '1.1rem', padding: '0.75rem 1rem' }}
                      required
                      disabled={loading}
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="btn btn-primary btn-lg px-5 d-flex align-items-center justify-content-center gap-2"
                    style={{ whiteSpace: 'nowrap' }}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        <span>Buscando...</span>
                      </>
                    ) : (
                      <>
                        <i className="bi bi-search"></i>
                        <span>Buscar</span>
                      </>
                    )}
                  </button>
                </div>
              </form>

              {loading && (
                <div
                  className="mt-4 p-3 rounded border bg-light"
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <p className="small fw-semibold text-primary mb-2 mb-md-1">
                    <i className="bi bi-lightbulb me-2" aria-hidden="true" />
                    ¿Sabías que…?
                  </p>
                  <p
                    className="small text-muted mb-0"
                    key={sabiasIndex}
                    style={{ whiteSpace: 'pre-line' }}
                  >
                    {SABIAS_TIPS_BUSQUEDA[sabiasIndex]}
                  </p>
                </div>
              )}

              {/* Mensaje de error */}
              {error && (
                <div className="alert alert-danger mt-4" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}

              {/* Resultados */}
              {resultados && (
                <div className="mt-4">
                  {resultados.sinMultas || resultados.cantidadMultas === 0 ? (
                    <div className="alert alert-success" role="alert">
                      <i className="bi bi-check-circle me-2"></i>
                      <strong>¡Excelente!</strong> No se encontraron fotomultas para la patente <strong>{resultados.patente}</strong>.
                    </div>
                  ) : (
                    <div>
                      <div className="alert alert-warning mb-3" role="alert">
                        <h5 className="alert-heading mb-0">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          Se encontraron {resultados.cantidadMultas}{' '}
                          {resultados.cantidadMultas === 1 ? 'infracción' : 'infracciones'}
                        </h5>
                      </div>

                      <div className="alert alert-info mb-4 py-2" role="status">
                        <p className="mb-0 fw-semibold">
                          <i className="bi bi-info-circle me-2"></i>
                          Informe abreviado
                        </p>
                      </div>

                      {resultados.hayMasEnPantalla && (
                        <p className="text-muted small mb-3">
                          Mostramos {resultados.multasMostradas.length} de {resultados.cantidadMultas}{' '}
                          {resultados.cantidadMultas === 1 ? 'infracción' : 'infracciones'} en esta vista.
                        </p>
                      )}

                      <div className="list-group mb-4">
                        {resultados.multasMostradas.map((multa, index) => (
                          <div key={index} className="list-group-item mb-2 border rounded">
                            <div className="d-flex align-items-start">
                              <div className="flex-grow-1">
                                <h6 className="mb-2">
                                  <i className="bi bi-file-text me-2 text-primary"></i>
                                  Infracción #{index + 1}
                                </h6>
                                {multa.descripcion ? (
                                  <p className="mb-0 text-muted">
                                    {multa.descripcion}
                                  </p>
                                ) : (
                                  <p className="mb-0 text-muted">
                                    Infracción de tránsito
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="card border-primary shadow-sm mt-4">
                    <div className="card-header bg-primary text-white">
                          <h5 className="mb-0">
                            <i className="bi bi-whatsapp me-2"></i>
                            Recibí el informe <strong>completo</strong> por WhatsApp
                          </h5>
                      <p className="mb-0 mt-2 small opacity-90">
                        Para recibir el informe completo, completá el formulario.
                      </p>
                    </div>
                    <div className="card-body">
                      {whatsappNotice && whatsappNotice.variant === 'success' && (
                        <div
                          className="d-flex align-items-center gap-3 mb-3 p-3 rounded-3 border border-success border-opacity-50 bg-success bg-opacity-10"
                          role="status"
                        >
                          <div
                            className="rounded-circle bg-success d-flex align-items-center justify-content-center flex-shrink-0 shadow-sm"
                            style={{ width: '3rem', height: '3rem' }}
                            aria-hidden="true"
                          >
                            <i className="bi bi-check-lg text-white" style={{ fontSize: '1.35rem' }} />
                          </div>
                          <p className="mb-0 fw-semibold text-success">{whatsappNotice.message}</p>
                        </div>
                      )}
                      {whatsappNotice && whatsappNotice.variant === 'warning' && (
                        <div className="alert alert-warning mb-3" role="alert">
                          <i className="bi bi-info-circle me-2" aria-hidden="true" />
                          {whatsappNotice.message}
                        </div>
                      )}
                      {whatsappNotice && whatsappNotice.variant === 'danger' && (
                        <div className="alert alert-danger mb-3" role="alert">
                          <i className="bi bi-exclamation-triangle me-2" aria-hidden="true" />
                          {whatsappNotice.message}
                        </div>
                      )}
                      <form onSubmit={handleWhatsappSubmit}>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label htmlFor="nombre" className="form-label">
                              Nombre completo <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="nombre"
                              value={formData.nombre}
                              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                              required
                              placeholder="Juan Pérez"
                              disabled={whatsappLoading}
                            />
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="telefono" className="form-label">
                              Número de teléfono <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                              <span className="input-group-text">+54 9</span>
                              <input
                                type="tel"
                                className="form-control"
                                id="telefono"
                                value={formData.telefono}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    telefono: e.target.value.replace(/\D/g, ''),
                                  })
                                }
                                required
                                placeholder="11 1234 5678"
                                disabled={whatsappLoading}
                                inputMode="numeric"
                                autoComplete="tel"
                              />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <label htmlFor="cupon" className="form-label">
                              Cupón de descuento <span className="text-muted fw-normal">(opcional)</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="cupon"
                              value={formData.cupon}
                              onChange={(e) => setFormData({ ...formData, cupon: e.target.value })}
                              placeholder={
                                isMobileCompact
                                  ? 'Código'
                                  : 'Si tenés un código, ingresalo acá'
                              }
                              maxLength={32}
                              disabled={whatsappLoading}
                              autoComplete="off"
                            />
                          </div>
                          <div className="col-12 col-md-6">
                            <p className="form-label mb-1 text-muted small">Tipo de cliente</p>
                            <p className="mb-0 fw-medium">Particulares</p>
                            <input type="hidden" name="tipoCliente" value={formData.tipoCliente} />
                          </div>
                          <div className="col-12">
                            <button
                              type="submit"
                              className="btn btn-success w-100 d-flex align-items-center justify-content-center gap-2"
                              disabled={whatsappLoading}
                            >
                              {whatsappLoading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                  Enviando…
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-whatsapp"></i>
                                  Enviar por WhatsApp
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConsultaMultas;

